import type { Cell, Grid } from "@/lib/api";

import { type GridCells, rowY, cellCentre } from "./maze-geometry";

/**
 * The parts of a maze drawing that every view shares: the floor, the walls, the spawn and
 * the target. The route editor layers coverage and paths on top; the new-maze preview shows
 * just these.
 *
 * Two facts about the grid decide how it is drawn. Cell `(row, col)` has its centre at world
 * `(col * 0.2, row * 0.2)` with `+y` up, so row 0 is at the *bottom* and the SVG flips rows
 * (`rowY`). And a `#` cell is not a solid block: physically the walls are thin panels (4 mm)
 * running along the grid's wall lines, with rooms on the odd indices. Each `#` is therefore
 * drawn as a stroke to every neighbouring `#`, which reproduces the lines the textured
 * top-down render shows. Drawing them as full squares -- the first version did -- made a
 * maze of corridors look like a maze of fat pillars and hid where the doorways were.
 */

export const FLOOR = "#f4f1ea";
export const WALL = "#3b3b3b";
export const WALL_THICKNESS = 0.28;
export const SPAWN_COLOUR = "#16a34a";
export const TARGET_COLOUR = "#dc2626";

/**
 * One line between the centres of every pair of adjacent `#` cells, plus a post for
 * isolated ones: continuous strokes, no seams between rectangles.
 */
export function Walls({ grid }: { grid: GridCells }) {
  return (
    <g stroke={WALL} strokeWidth={WALL_THICKNESS} strokeLinecap="square" fill={WALL}>
      {grid.cells.map((rowText, row) =>
        Array.from(rowText).map((ch, col) => {
          if (ch !== "#") return null;
          const right = grid.cells[row]?.[col + 1] === "#";
          const up = grid.cells[row + 1]?.[col] === "#";
          const left = grid.cells[row]?.[col - 1] === "#";
          const down = grid.cells[row - 1]?.[col] === "#";
          const mx = col + 0.5;
          const my = rowY(grid, row) + 0.5;
          return (
            <g key={`w${row}-${col}`}>
              {right ? <line x1={mx} y1={my} x2={mx + 1} y2={my} /> : null}
              {up ? <line x1={mx} y1={my} x2={mx} y2={my - 1} /> : null}
              {!right && !up && !left && !down ? (
                <rect x={mx - WALL_THICKNESS / 2} y={my - WALL_THICKNESS / 2} width={WALL_THICKNESS} height={WALL_THICKNESS} stroke="none" />
              ) : null}
            </g>
          );
        }),
      )}
    </g>
  );
}

/** The green spawn disc and the red target ring, the same marks `map.png` uses. */
export function Markers({ grid, spawn, target }: { grid: Pick<Grid, "rows">; spawn: Cell; target: Cell }) {
  const s = cellCentre(grid, spawn);
  const t = cellCentre(grid, target);
  return (
    <>
      <circle cx={s.x} cy={s.y} r={0.42} fill={SPAWN_COLOUR} stroke="white" strokeWidth={0.08} />
      <circle cx={t.x} cy={t.y} r={0.42} fill="none" stroke={TARGET_COLOUR} strokeWidth={0.18} />
      <circle cx={t.x} cy={t.y} r={0.12} fill={TARGET_COLOUR} />
    </>
  );
}

/** The legend entries for the markers, for under any maze drawing. */
export function MarkerLegend() {
  return (
    <>
      <span>
        <span className="mr-1 inline-block size-2.5 rounded-full bg-[#16a34a] align-middle" /> spawn
      </span>
      <span>
        <span className="mr-1 inline-block size-2.5 rounded-full border-2 border-[#dc2626] align-middle" /> target
      </span>
    </>
  );
}
